/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Inbox, Upload, FileText, Image as ImageIcon, AlertCircle, CheckCircle2,
  Trash2, RefreshCw, Loader2, X, Sparkles, Receipt, FolderOpen, ChevronDown,
} from 'lucide-react';

const KIND_META = {
  invoice: { label: 'Invoice', icon: Receipt, color: 'bg-blue-100 text-blue-700' },
  receipt: { label: 'Receipt', icon: Receipt, color: 'bg-blue-100 text-blue-700' },
  document_with_expiry: { label: 'Document (expiry)', icon: FolderOpen, color: 'bg-amber-100 text-amber-700' },
  document_general: { label: 'Document', icon: FolderOpen, color: 'bg-amber-100 text-amber-700' },
  bank_statement: { label: 'Bank statement', icon: FileText, color: 'bg-emerald-100 text-emerald-700' },
  payroll_csv: { label: 'Payroll CSV', icon: FileText, color: 'bg-purple-100 text-purple-700' },
  unknown: { label: 'Needs sorting', icon: AlertCircle, color: 'bg-muted text-muted-foreground' },
};

const DOC_TYPES = [
  'right_to_work', 'employment_contract', 'dbs_check',
  'food_hygiene_cert', 'allergen_awareness', 'first_aid_cert',
  'public_liability', 'employer_liability', 'product_liability',
  'premises_license', 'personal_license_dps', 'food_business_reg',
  'music_license', 'gas_safety', 'electrical_safety', 'pat_testing',
  'fire_risk', 'haccp', 'lease_agreement', 'supplier_contract',
  'equipment_warranty', 'other',
];
const CATEGORIES = ['employee', 'insurance', 'license', 'safety', 'legal', 'other'];

export default function InboxPage() {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ needs_review: 0, auto_processed: 0, discarded: 0 });
  const [activeTab, setActiveTab] = useState('needs_review');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/inbox');
      setItems(res.data?.items || []);
      setCounts(res.data?.counts || {});
    } catch (e) {
      toast.error('Failed to load inbox');
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchInbox(); }, [fetchInbox]);

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        const form = new FormData();
        form.append('file', file);
        await api.post('/inbox/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success(`Uploaded ${file.name}`);
      } catch (e) {
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    setUploading(false);
    fetchInbox();
  };

  const migrateBotUploads = async () => {
    try {
      const res = await api.post('/inbox/migrate-bot-uploads');
      const migrated = res.data?.migrated || 0;
      toast.success(migrated > 0 ? `Migrated ${migrated} legacy bot upload(s)` : 'No legacy uploads to migrate');
      fetchInbox();
    } catch (e) {
      toast.error('Migration failed');
    }
  };

  const filtered = items.filter((i) => i.status === activeTab);

  return (
    <div className="space-y-6" data-testid="inbox-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><Inbox className="w-7 h-7" /> Inbox</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Drop any paperwork here — invoices, certificates, contracts, photos. AI classifies and routes them automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={migrateBotUploads} title="Migrate legacy bot uploads into the inbox" data-testid="migrate-bot-uploads">
            <RefreshCw className="w-4 h-4 mr-1" /> Migrate bot uploads
          </Button>
          <Button variant="ghost" size="sm" onClick={fetchInbox} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <UploadDropZone uploading={uploading} onUpload={handleUpload} />

      <div className="flex items-center gap-1 border-b" data-testid="inbox-tabs">
        {[
          { key: 'needs_review', label: 'Needs review', count: counts.needs_review },
          { key: 'auto_processed', label: 'Auto-processed', count: counts.auto_processed },
          { key: 'discarded', label: 'Discarded', count: counts.discarded },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-sm border-b-2 transition-colors ${activeTab === t.key ? 'border-primary text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            data-testid={`tab-${t.key}`}
          >
            {t.label} {t.count > 0 && <span className="ml-1 text-xs">({t.count})</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          {activeTab === 'needs_review' ? '🎉 Inbox zero. Nothing to sort.' :
           activeTab === 'auto_processed' ? 'No auto-processed items yet.' :
           'No discarded items.'}
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((item) => (
            <InboxRow key={item.id} item={item} onSelect={() => setSelected(item)} />
          ))}
        </div>
      )}

      {selected && (
        <InboxDetailDrawer
          item={selected}
          onClose={() => setSelected(null)}
          onChanged={() => { setSelected(null); fetchInbox(); }}
        />
      )}
    </div>
  );
}

function UploadDropZone({ uploading, onUpload }) {
  const [dragOver, setDragOver] = useState(false);
  return (
    <Card
      className={`border-2 border-dashed transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-border'}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        onUpload(Array.from(e.dataTransfer.files || []));
      }}
      data-testid="upload-dropzone"
    >
      <CardContent className="py-8 text-center">
        <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
        <p className="font-medium">Drop files here or click to upload</p>
        <p className="text-xs text-muted-foreground mt-1">
          Photos, PDFs, CSVs — invoices, certificates, contracts, bank statements. Multiple files supported.
        </p>
        <input
          type="file"
          multiple
          className="hidden"
          id="inbox-file-input"
          onChange={(e) => onUpload(Array.from(e.target.files || []))}
          accept="image/*,application/pdf,text/csv,text/plain,.heic,.heif"
          data-testid="inbox-file-input"
        />
        <Button asChild className="mt-3" disabled={uploading}>
          <label htmlFor="inbox-file-input" className="cursor-pointer">
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Choose files</>}
          </label>
        </Button>
      </CardContent>
    </Card>
  );
}

function InboxRow({ item, onSelect }) {
  const c = item.classification || {};
  const meta = KIND_META[c.kind] || KIND_META.unknown;
  const Icon = meta.icon;
  const conf = Math.round((c.confidence || 0) * 100);
  const sourceLabel = {
    web: 'Web', telegram: 'Telegram', email: 'Email', bot_uploads_legacy: 'Legacy bot',
  }[item.source] || item.source;

  return (
    <button
      onClick={onSelect}
      className="w-full text-left rounded-lg border bg-card hover:bg-accent/50 transition-colors p-3 flex items-center gap-3"
      data-testid={`inbox-row-${item.id}`}
    >
      <div className="shrink-0">
        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${meta.color}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium truncate">{c.title || item.filename}</span>
          <Badge variant="outline" className={`text-[10px] ${meta.color}`}>{meta.label}</Badge>
          {c.learnt_match && (
            <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
              <Sparkles className="w-2.5 h-2.5 mr-0.5" /> learnt
            </Badge>
          )}
          {item.status === 'auto_processed' && (
            <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700">auto</Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 truncate">
          {c.vendor && <span>{c.vendor} · </span>}
          {sourceLabel} · {new Date(item.created_at).toLocaleString('en-GB')}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className={`text-xs font-mono ${conf >= 75 ? 'text-emerald-600' : conf >= 40 ? 'text-amber-600' : 'text-muted-foreground'}`}>
          {conf}%
        </div>
        <div className="text-[10px] text-muted-foreground">confidence</div>
      </div>
      <ChevronDown className="w-4 h-4 -rotate-90 text-muted-foreground" />
    </button>
  );
}

function InboxDetailDrawer({ item, onClose, onChanged }) {
  const { api } = useAuth();
  const c = item.classification || {};
  const [form, setForm] = useState({
    target_kind: c.kind === 'invoice' || c.kind === 'receipt' ? 'invoice'
      : c.kind === 'document_with_expiry' || c.kind === 'document_general' ? 'document'
      : 'document',
    title: c.title || item.filename,
    document_type: c.suggested_doc_type || 'other',
    category: c.suggested_category || 'other',
    expiry_date: c.expiry_date || '',
    vendor: c.vendor || '',
    amount: c.invoice_total || '',
    invoice_date: c.invoice_date || '',
    notes: item.user_note || '',
  });
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewMime, setPreviewMime] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  // Fetch the file as a blob via the authenticated axios client so the
  // browser's <iframe>/<img> tags (which can't send auth headers) get a
  // local blob:// URL. Also handles HEIC since the backend transcodes to JPEG.
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/inbox/${item.id}/file`, { responseType: 'blob' });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        revoke = url;
        setPreviewUrl(url);
        setPreviewMime(res.data?.type || item.mime_type);
      } catch (e) {
        if (!cancelled) setPreviewError('Preview unavailable');
      }
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [api, item.id, item.mime_type]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (form.target_kind === 'invoice') body.amount = parseFloat(body.amount) || 0;
      await api.post(`/inbox/${item.id}/accept`, body);
      toast.success('Saved');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const reclassify = async (newKind) => {
    try {
      await api.post(`/inbox/${item.id}/reclassify`, { kind: newKind });
      toast.success(`Reclassified as ${newKind}`);
      setForm((f) => ({
        ...f,
        target_kind: newKind === 'invoice' || newKind === 'receipt' ? 'invoice'
          : newKind === 'document_with_expiry' || newKind === 'document_general' ? 'document' : 'document',
      }));
    } catch { toast.error('Reclassify failed'); }
  };

  const discard = async () => {
    if (!window.confirm('Discard this item?')) return;
    try {
      await api.delete(`/inbox/${item.id}`);
      toast.success('Discarded');
      onChanged();
    } catch { toast.error('Discard failed'); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-stretch justify-end" onClick={onClose}>
      <div className="bg-background w-full max-w-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="inbox-drawer">
        <div className="sticky top-0 bg-background border-b flex items-center justify-between p-4 z-10">
          <h3 className="font-semibold truncate flex items-center gap-2">
            <Inbox className="w-4 h-4" /> {c.title || item.filename}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Preview */}
          <div className="rounded-lg border overflow-hidden bg-muted/30">
            {previewError ? (
              <div className="p-6 text-center text-sm text-muted-foreground" data-testid="preview-error">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                {previewError}
              </div>
            ) : !previewUrl ? (
              <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
            ) : (previewMime || '').startsWith('image/') ? (
              <img src={previewUrl} alt={item.filename} className="w-full max-h-96 object-contain" data-testid="preview-image" />
            ) : (previewMime === 'application/pdf') ? (
              <iframe src={previewUrl} title="preview" className="w-full h-96 bg-white" data-testid="preview-pdf" />
            ) : (
              <div className="p-6 text-center text-sm text-muted-foreground">
                <FileText className="w-8 h-8 mx-auto mb-2" />
                <a href={previewUrl} download={item.filename} className="underline" data-testid="preview-download">
                  Download {item.filename}
                </a>
              </div>
            )}
          </div>

          {/* Classification */}
          <div className="rounded-lg border p-3 bg-muted/30" data-testid="classification-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">AI Classification</div>
              <Badge variant="outline" className="text-[10px]">
                {Math.round((c.confidence || 0) * 100)}% confident
              </Badge>
            </div>
            <div className="text-sm">
              <div>Kind: <strong>{(KIND_META[c.kind] || KIND_META.unknown).label}</strong></div>
              {c.vendor && <div>Vendor: {c.vendor}</div>}
              {c.invoice_total != null && <div>Amount: £{Number(c.invoice_total).toFixed(2)}</div>}
              {c.expiry_date && <div>Expires: {c.expiry_date}</div>}
            </div>
            <div className="mt-2 flex gap-1 flex-wrap">
              <span className="text-[10px] text-muted-foreground self-center">Re-classify:</span>
              {['invoice', 'document_with_expiry', 'document_general', 'unknown'].map((k) => (
                <Button key={k} size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => reclassify(k)} data-testid={`reclassify-${k}`}>
                  {(KIND_META[k] || KIND_META.unknown).label}
                </Button>
              ))}
            </div>
          </div>

          {/* Target form */}
          <div className="space-y-3">
            <div className="flex gap-2">
              {['document', 'invoice', 'discard'].map((t) => (
                <button
                  key={t}
                  onClick={() => setForm((f) => ({ ...f, target_kind: t }))}
                  className={`px-3 py-1.5 rounded text-xs font-medium border ${form.target_kind === t ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:text-foreground'}`}
                  data-testid={`target-${t}`}
                >
                  {t === 'document' ? 'Save as Document' : t === 'invoice' ? 'Save as Invoice' : 'Discard'}
                </button>
              ))}
            </div>

            {form.target_kind === 'document' && (
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="form-title" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Document type</Label>
                    <select className="w-full h-9 px-2 border rounded-md bg-background text-sm" value={form.document_type} onChange={(e) => setForm({ ...form, document_type: e.target.value })} data-testid="form-doc-type">
                      {DOC_TYPES.map((t) => <option key={t} value={t}>{t.replaceAll('_', ' ')}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Category</Label>
                    <select className="w-full h-9 px-2 border rounded-md bg-background text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="form-category">
                      {CATEGORIES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Expiry date (optional)</Label>
                  <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="form-expiry" />
                </div>
                <div>
                  <Label className="text-xs">Notes (optional)</Label>
                  <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="form-notes" />
                </div>
              </div>
            )}

            {form.target_kind === 'invoice' && (
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Vendor</Label>
                  <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="form-vendor" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Amount (£)</Label>
                    <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="form-amount" />
                  </div>
                  <div>
                    <Label className="text-xs">Invoice date</Label>
                    <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="form-invoice-date" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="form-invoice-notes" />
                </div>
              </div>
            )}

            {form.target_kind === 'discard' && (
              <div className="rounded p-3 text-xs bg-red-50 border border-red-200 text-red-800">
                This item will be marked as discarded. You can still view it later under the "Discarded" tab.
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-background border-t p-4 flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={discard} className="text-red-600" data-testid="drawer-discard">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Discard
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving || item.status !== 'needs_review'} data-testid="drawer-save">
              {saving && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
              {form.target_kind === 'discard' ? 'Confirm discard' : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Confirm & save</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
